/**
 * Forms API Contracts — submissions (results surface)
 *
 * Read-side contracts for the responses table + single-response view.
 * The list response deliberately EXCLUDES PII blobs (payloadJson,
 * schemaSnapshotJson, ipAddress, signatureData) — those are detail-view /
 * CSV-export only. See plan U4/U5.
 */

import { z } from 'zod'
import { FormSubmitterTypeSchema } from '../../schemas/entities/form'

// ============================================
// Submission list item (PII-excluded)
// ============================================

export const FormSubmissionListItemSchema = z.object({
  /** Submission publicId. */
  id: z.string(),
  /** ISO submission timestamp. */
  submittedAt: z.string(),
  submitterType: FormSubmitterTypeSchema,
  /** Display name: waiver signer, linked user, or null for plain anonymous. */
  submitterName: z.string().nullable(),
  /** Whether the submission carries a typed signature (consent surfaces). */
  signed: z.boolean(),
  /** Short, truncated preview of the first answers (admin-only convenience). */
  summary: z.string().nullable()
})
export type FormSubmissionListItem = z.infer<typeof FormSubmissionListItemSchema>

export const ListFormSubmissionsResponseSchema = z.object({
  data: z.array(FormSubmissionListItemSchema),
  meta: z.object({
    total: z.number(),
    limit: z.number(),
    offset: z.number()
  })
})
export type ListFormSubmissionsResponse = z.infer<typeof ListFormSubmissionsResponseSchema>

// ============================================
// Submission detail (single response — admin/PII view)
// ============================================

/**
 * The full submission for the single-response slide-over. Includes the
 * answer payload + the schema snapshot the answers are rendered against
 * (NOT the live form schema — historical readability). Excludes ipAddress
 * and raw signature image data; the typed signature name is included for
 * display. `schemaSnapshot` is `unknown` (cast to FormSchema by the client)
 * matching the `FormDetail.schema` convention.
 */
// ============================================
// Recipients status (signed-vs-outstanding — R8)
// ============================================

/** One recipient family's signing status (admin-only). */
export const FormRecipientStatusSchema = z.object({
  /** The recipient member's UsersOnSchools publicId. */
  id: z.string(),
  name: z.string(),
  email: z.string().nullable(),
  signed: z.boolean(),
  /** true = authenticated submitter matched by id; false = email-only (spoofable) match. */
  verified: z.boolean()
})
export type FormRecipientStatus = z.infer<typeof FormRecipientStatusSchema>

/** An attributed signer that matched no recipient family — review-needed. */
export const FormUnmatchedSignerSchema = z.object({
  signerEmail: z.string(),
  signerName: z.string().nullable()
})
export type FormUnmatchedSigner = z.infer<typeof FormUnmatchedSignerSchema>

export const FormRecipientsStatusSchema = z.object({
  signed: z.array(FormRecipientStatusSchema),
  outstanding: z.array(FormRecipientStatusSchema),
  unmatchedSigners: z.array(FormUnmatchedSignerSchema),
  counts: z.object({
    total: z.number(),
    signed: z.number(),
    outstanding: z.number(),
    verified: z.number(),
    unverified: z.number(),
    unmatched: z.number()
  })
})
export type FormRecipientsStatus = z.infer<typeof FormRecipientsStatusSchema>

export const FormSubmissionDetailSchema = z.object({
  id: z.string(),
  submittedAt: z.string(),
  submitterType: FormSubmitterTypeSchema,
  submitterName: z.string().nullable(),
  submitterEmail: z.string().nullable(),
  signatureText: z.string().nullable(),
  payload: z.record(z.string(), z.unknown()),
  schemaSnapshot: z.unknown()
})
export type FormSubmissionDetail = z.infer<typeof FormSubmissionDetailSchema>

// ============================================
// Response envelopes
// ============================================

/** GET /v1/forms/:formId/recipients */
export const GetFormRecipientsStatusResponseSchema = z.object({
  data: FormRecipientsStatusSchema
})
export type GetFormRecipientsStatusResponse = z.infer<typeof GetFormRecipientsStatusResponseSchema>

/** GET /v1/forms/:formId/submissions/:submissionId */
export const GetFormSubmissionResponseSchema = z.object({
  data: FormSubmissionDetailSchema
})
export type GetFormSubmissionResponse = z.infer<typeof GetFormSubmissionResponseSchema>

/**
 * POST /v1/forms/:formId/duplicate — the new DRAFT copy. Only `publicId` is
 * declared: the caller navigates to the copy's builder and reads nothing else.
 */
export const DuplicateFormResponseSchema = z.object({
  message: z.string(),
  data: z.object({ publicId: z.string() })
})
export type DuplicateFormResponse = z.infer<typeof DuplicateFormResponseSchema>
